/**
 * Sales Competition Dashboard
 * 
 * Features:
 * - Agent Profile Card with level, titles, and rank
 * - 3 Competition Panels (Deal Closer, Lead Converter, AI Challenge)
 * - AI Challenge Card with progress tracking
 * - Weekly Countdown Timer
 * - Full Leaderboard with tabs
 * - HOD Controls (bonus points, view all challenges)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Trophy,
  Zap,
  Target,
  Crown,
  Star,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  Award,
  Users,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Gift,
  Sparkles,
  Medal,
  Timer,
  Flame
} from 'lucide-react';
import HelpInfo from '../components/HelpInfo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Level Badge Colors
const LEVEL_COLORS = {
  'Rookie': 'bg-slate-500',
  'Rising Star': 'bg-blue-500',
  'Hustler': 'bg-purple-500',
  'Closer': 'bg-amber-500',
  'Elite': 'bg-emerald-500',
  'Legend': 'bg-gradient-to-r from-amber-500 to-red-500'
};

// Competition Icons
const COMPETITION_ICONS = {
  deal_closer: Trophy,
  lead_converter: Zap,
  ai_challenge: Target
};

// ============ Weekly Countdown Timer ============
const WeeklyCountdown = ({ timeRemaining }) => {
  const { darkMode } = useTheme();
  
  if (!timeRemaining) return null;
  
  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl ${
      darkMode ? 'bg-violet-900/20 border border-violet-700/50' : 'bg-violet-50 border border-violet-200'
    }`}>
      <Timer className="text-violet-500" size={24} />
      <div>
        <p className={`text-sm font-medium ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>
          Week Ends In
        </p>
        <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
          {timeRemaining.display}
        </p>
      </div>
    </div>
  );
};

// ============ Agent Profile Card ============
const AgentProfileCard = ({ profile, weekInfo }) => {
  const { darkMode } = useTheme();
  
  if (!profile) return null;
  
  const levelColor = LEVEL_COLORS[profile.level] || 'bg-slate-500';
  
  return (
    <Card className={`${darkMode ? 'bg-gradient-to-br from-violet-900/30 to-slate-900 border-violet-700/50' : 'bg-gradient-to-br from-violet-50 to-white'}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className="relative">
            <div className={`w-20 h-20 rounded-full ${levelColor} flex items-center justify-center text-white text-2xl font-bold shadow-lg`}>
              {profile.agent_name?.charAt(0) || '?'}
            </div>
            {profile.active_titles?.includes('Agent of the Week 👑') && (
              <Crown className="absolute -top-2 -right-2 text-amber-400" size={24} />
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {profile.agent_name}
              </h2>
              <Badge className={`${levelColor} text-white`}>
                {profile.level}
              </Badge>
            </div>
            
            {/* Active Titles */}
            {profile.active_titles?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {profile.active_titles.map((title, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs border-amber-500 text-amber-500">
                    {title}
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-white shadow-sm'}`}>
                <p className={`text-2xl font-bold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                  {profile.weekly_points?.total || 0}
                </p>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  This Week
                </p>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-white shadow-sm'}`}>
                <p className={`text-2xl font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  {profile.all_time_points || 0}
                </p>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  All-Time
                </p>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-white shadow-sm'}`}>
                <p className={`text-2xl font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                  #{profile.weekly_rank || '-'}
                </p>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  of {profile.total_agents} agents
                </p>
              </div>
            </div>
          </div>
          
          {/* Countdown */}
          <div className="hidden lg:block">
            <WeeklyCountdown timeRemaining={weekInfo?.time_remaining} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ Competition Panel ============
const CompetitionPanel = ({ competition, competitionKey, points, rank, topAgents, breakdown }) => {
  const { darkMode } = useTheme();
  const Icon = COMPETITION_ICONS[competitionKey] || Trophy;
  
  const colors = {
    deal_closer: { bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-500' },
    lead_converter: { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-500' },
    ai_challenge: { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-500' }
  };
  
  const color = colors[competitionKey] || colors.deal_closer;
  
  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${darkMode ? color.bg + '/20' : color.light}`}>
              <Icon className={color.text} size={20} />
            </div>
            <CardTitle className={`text-base ${darkMode ? 'text-white' : ''}`}>{competition?.name}</CardTitle>
          </div>
          <Badge variant="outline" className={color.text}>
            #{rank || '-'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Points */}
        <div className="text-center">
          <p className={`text-4xl font-bold ${color.text}`}>{points || 0}</p>
          <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>points this week</p>
        </div>
        
        {/* Mini Leaderboard */}
        {topAgents?.length > 0 && (
          <div className="space-y-2">
            <p className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Top 3
            </p>
            {topAgents.slice(0, 3).map((agent, idx) => (
              <div 
                key={agent.agent_id}
                className={`flex items-center justify-between p-2 rounded text-sm ${
                  darkMode ? 'bg-muted/50' : 'bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-amber-500 text-white' : 
                    idx === 1 ? 'bg-slate-400 text-white' : 
                    'bg-amber-700 text-white'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className={darkMode ? 'text-white' : 'text-slate-700'}>
                    {agent.agent_name}
                  </span>
                </div>
                <span className={`font-semibold ${color.text}`}>
                  {agent[`${competitionKey}_points`] || 0}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {/* Points Breakdown */}
        {breakdown?.length > 0 && (
          <div className="space-y-1">
            <p className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Your Points
            </p>
            <ScrollArea className="h-24">
              {breakdown.filter(b => b.competition === competitionKey).slice(0, 5).map((item, idx) => (
                <div key={idx} className={`flex justify-between text-xs py-1 ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  <span>{item.reason}</span>
                  <span className={`font-medium ${color.text}`}>+{item.points}</span>
                </div>
              ))}
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ AI Challenge Card ============
const AIChallengeCard = ({ challenge, onRefresh, streak = 0 }) => {
  const { darkMode } = useTheme();

  if (!challenge) {
    return (
      <Card className={`${darkMode ? 'bg-gradient-to-br from-purple-900/30 to-slate-900 border-purple-700/50' : 'bg-gradient-to-br from-purple-50 to-white'}`}>
        <CardContent className="p-6 text-center">
          <Sparkles className="mx-auto mb-4 text-purple-500" size={48} />
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            AI Challenge Not Generated Yet
          </h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Your personalized challenge will be generated soon.
          </p>
          <Button onClick={onRefresh} variant="outline">
            <RefreshCw size={16} className="mr-2" />
            Check Again
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  const progress = challenge.target_value > 0
    ? Math.min(100, (challenge.current_progress / challenge.target_value) * 100)
    : 0;
  const pct = Math.round(progress);
  const remaining = Math.max(
    0,
    (challenge.target_value || 0) - (challenge.current_progress || 0),
  );

  // Days left before the week resets (Monday) and the challenge expires —
  // a countdown gives the card urgency instead of an open-ended goal.
  const daysLeft = (() => {
    if (!challenge.week_start_date) return null;
    const start = new Date(`${challenge.week_start_date}T00:00:00`);
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86400000));
  })();

  return (
    <Card className={`${darkMode ? 'bg-gradient-to-br from-purple-900/30 to-slate-900 border-purple-700/50' : 'bg-gradient-to-br from-purple-50 to-white'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Target className="text-purple-500" size={24} />
            <CardTitle className="text-lg">AI Challenge</CardTitle>
            {/* Streak badge — rewards consistency, not just one good week. */}
            {streak >= 2 && (
              <Badge className="bg-orange-100 text-orange-700 border border-orange-300 text-xs">
                <Flame size={11} className="mr-0.5" /> {streak}-week streak
              </Badge>
            )}
          </div>
          <Badge variant={challenge.is_completed ? 'success' : 'secondary'}>
            {challenge.is_completed ? 'Completed ✓' : 'In Progress'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            {challenge.challenge_title}
          </h4>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            {challenge.challenge_description}
          </p>
        </div>
        
        {/* Progress */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
              Progress: {challenge.current_progress} / {challenge.target_value}
              <span className="ml-1 text-xs opacity-70">({pct}%)</span>
            </span>
            <span className="text-purple-500 font-semibold">
              +{challenge.points_reward} pts
            </span>
          </div>
          <Progress value={progress} className="h-3" />
          {/* Motivational microcopy + week countdown — only while open. */}
          {!challenge.is_completed && (
            <div className="flex justify-between items-center mt-1.5 text-xs">
              <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                {challenge.current_progress === 0
                  ? 'Not started yet — make your first move.'
                  : remaining === 1
                    ? 'Just 1 more to go — almost there!'
                    : `${remaining} more to go — keep it up!`}
              </span>
              {daysLeft != null && (
                <span
                  className={`flex items-center gap-1 font-medium ${
                    daysLeft <= 1
                      ? 'text-red-500'
                      : daysLeft <= 2
                        ? 'text-amber-500'
                        : darkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  <Clock size={11} />
                  {daysLeft === 0 ? 'Last day' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
                </span>
              )}
            </div>
          )}
        </div>

        {challenge.is_completed && (
          <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-emerald-900/30' : 'bg-emerald-50'}`}>
            <Award className="mx-auto text-emerald-500 mb-2" size={32} />
            <p className={`font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
              Challenge Completed! +{challenge.points_reward} points earned
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ Full Leaderboard ============
const FullLeaderboard = ({ leaderboard, currentUserId, activeTab, onTabChange }) => {
  const { darkMode } = useTheme();
  
  const getPointsField = (tab) => {
    switch (tab) {
      case 'deal_closer': return 'deal_closer_points';
      case 'lead_converter': return 'lead_converter_points';
      case 'ai_challenge': return 'ai_challenge_points';
      default: return 'total_weekly_points';
    }
  };
  
  const sortedLeaderboard = [...(leaderboard || [])].sort((a, b) => {
    const field = getPointsField(activeTab);
    return (b[field] || 0) - (a[field] || 0);
  });
  
  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className={`text-lg flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
          <Medal size={20} />
          Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList className="w-full mb-4">
            <TabsTrigger value="overall" className="flex-1">Overall</TabsTrigger>
            <TabsTrigger value="deal_closer" className="flex-1">Deal Closer</TabsTrigger>
            <TabsTrigger value="lead_converter" className="flex-1">Lead Converter</TabsTrigger>
            <TabsTrigger value="ai_challenge" className="flex-1">AI Challenge</TabsTrigger>
          </TabsList>
          
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {sortedLeaderboard.length === 0 ? (
                <div className={`text-center py-12 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No competition data yet this week</p>
                </div>
              ) : (
                sortedLeaderboard.map((agent, idx) => {
                  const pointsField = getPointsField(activeTab);
                  const isCurrentUser = agent.agent_id === currentUserId;
                  
                  return (
                    <div 
                      key={agent.agent_id}
                      className={`flex items-center gap-4 p-3 rounded-lg ${
                        isCurrentUser 
                          ? darkMode ? 'bg-violet-900/30 border border-violet-700/50' : 'bg-violet-50 border border-violet-200'
                          : darkMode ? 'bg-muted/30' : 'bg-slate-50'
                      }`}
                    >
                      {/* Rank */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        idx === 0 ? 'bg-amber-500 text-white' :
                        idx === 1 ? 'bg-slate-400 text-white' :
                        idx === 2 ? 'bg-amber-700 text-white' :
                        darkMode ? 'bg-muted text-muted-foreground' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {idx === 0 && activeTab === 'overall' ? <Crown size={16} /> : idx + 1}
                      </div>
                      
                      {/* Avatar & Name */}
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`w-10 h-10 rounded-full ${LEVEL_COLORS[agent.level] || 'bg-slate-500'} flex items-center justify-center text-white font-medium`}>
                          {agent.agent_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            {agent.agent_name}
                            {isCurrentUser && <span className="text-violet-500 ml-2">(You)</span>}
                          </p>
                          <Badge variant="outline" className="text-xs">
                            {agent.level}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Points */}
                      <div className="text-right">
                        <p className={`text-xl font-bold ${
                          activeTab === 'deal_closer' ? 'text-amber-500' :
                          activeTab === 'lead_converter' ? 'text-blue-500' :
                          activeTab === 'ai_challenge' ? 'text-purple-500' :
                          'text-violet-500'
                        }`}>
                          {agent[pointsField] || 0}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          points
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </Tabs>
      </CardContent>
    </Card>
  );
};

// ============ HOD Award Points Dialog ============
const HODAwardDialog = ({ isOpen, onClose, agents, onAward }) => {
  const { darkMode } = useTheme();
  const [formData, setFormData] = useState({
    agent_id: '',
    points: 50,
    competition: 'deal_closer',
    reason: ''
  });
  const [awarding, setAwarding] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agent_id || !formData.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    setAwarding(true);
    await onAward(formData);
    setAwarding(false);
    onClose();
    setFormData({ agent_id: '', points: 50, competition: 'deal_closer', reason: '' });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={darkMode ? 'bg-card border-border' : ''}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift size={20} />
            Award Bonus Points
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Select Agent *
            </label>
            <select
              value={formData.agent_id}
              onChange={(e) => setFormData({ ...formData, agent_id: e.target.value })}
              className={`w-full mt-1 px-3 py-2 rounded-md border ${
                darkMode ? 'bg-background border-border text-foreground' : 'bg-white border-slate-200'
              }`}
              required
            >
              <option value="">Choose agent...</option>
              {agents?.map((agent) => (
                <option key={agent.agent_id} value={agent.agent_id}>
                  {agent.agent_name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Points
              </label>
              <Input
                type="number"
                min={1}
                max={500}
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Competition
              </label>
              <select
                value={formData.competition}
                onChange={(e) => setFormData({ ...formData, competition: e.target.value })}
                className={`w-full mt-1 px-3 py-2 rounded-md border ${
                  darkMode ? 'bg-background border-border text-foreground' : 'bg-white border-slate-200'
                }`}
              >
                <option value="deal_closer">Deal Closer</option>
                <option value="lead_converter">Lead Converter</option>
                <option value="ai_challenge">AI Challenge</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Reason *
            </label>
            <Textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Why are you awarding these points?"
              rows={3}
              required
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={awarding}>
              {awarding ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Gift size={16} className="mr-2" />}
              Award Points
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============ Main Competition Dashboard ============
const CompetitionDashboard = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  
  // Detect dark mode from DOM as fallback
  const [actualDarkMode, setActualDarkMode] = React.useState(false);
  
  React.useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark') || 
                     window.matchMedia('(prefers-color-scheme: dark)').matches;
      setActualDarkMode(isDark);
    };
    checkDarkMode();
    
    // Listen for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);
  
  // Use either context or detected dark mode
  const darkMode = isDarkMode || actualDarkMode;
  
  // State
  const [profile, setProfile] = useState(null);
  const [weekInfo, setWeekInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [challengeStreak, setChallengeStreak] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeLeaderboardTab, setActiveLeaderboardTab] = useState('overall');
  
  // HOD State
  const [isHOD, setIsHOD] = useState(false);
  const [isAwardDialogOpen, setIsAwardDialogOpen] = useState(false);
  
  // Fetch all data
  // Progressive — each panel fills in as its endpoint returns. The
  // leaderboard query is the chunkiest of the four, so this is where it
  // mattered most: profile/week-info show up instantly while the heavier
  // aggregation loads in the background.
  const fetchData = useCallback(() => {
    if (!token) return;
    const headers = { 'Authorization': `Bearer ${token}` };
    const kick = (url, setter) => fetch(url, { headers })
      .then((r) => r.json())
      .then((d) => d?.success && setter(d))
      .catch((e) => console.error(`[CompetitionDashboard] ${url} failed:`, e));

    const tasks = [
      kick(`${API_URL}/api/competition/me`, (d) => setProfile(d.data)),
      kick(`${API_URL}/api/competition/week-info`, (d) => setWeekInfo(d.data)),
      kick(`${API_URL}/api/competition/leaderboard`, (d) => setLeaderboard(d.data?.leaderboard || [])),
      kick(`${API_URL}/api/competition/challenges/me`, (d) => {
        setChallenge(d.data?.challenge);
        setChallengeStreak(d.data?.streak || 0);
      }),
    ];

    // Role flag is local-only — no network — so set it immediately.
    setIsHOD(['hod', 'admin', 'super_admin', 'owner'].includes(user?.role));

    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  }, [token, user?.role]);
  
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);
  
  // Award points handler (HOD)
  const handleAwardPoints = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/api/competition/hod/award`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success(`Awarded ${formData.points} points!`);
        fetchData();
      } else {
        toast.error(result.detail || 'Failed to award points');
      }
    } catch (error) {
      toast.error('Failed to award points');
    }
  };
  
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50'}`}>
        <RefreshCw className="animate-spin text-violet-500" size={48} />
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50'}`} data-testid="competition-dashboard">
      <div className="max-w-[1800px] mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              <Trophy className="text-amber-500" size={28} />
              Sales Competition
              <HelpInfo
                align="inline"
                title="How the Sales Competition works"
                steps={[
                  <>Each week the sales floor competes across three contests — <strong>Deal Closer</strong>, <strong>Lead Converter</strong> and <strong>AI Challenge</strong> — and the week resets every Monday.</>,
                  <>Your profile card shows your level, rank and points earned this week across all three contests.</>,
                  <>The <strong>AI Challenge</strong> card is a personalised weekly goal aimed at your weakest area — its progress bar fills as you complete the tracked activity.</>,
                  <>The <strong>Leaderboard</strong> ranks everyone by total weekly points; switch the tab to see each individual contest.</>,
                  <>HODs can use <strong>Award Points</strong> to grant discretionary bonus points for results the system can't measure automatically.</>,
                ]}
                tip="Check your AI Challenge first thing each week — it's the fastest, most targeted way to climb the board."
              />
            </h1>
            <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
              Week of {weekInfo?.week_start_date}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isHOD && (
              <Button variant="outline" onClick={() => setIsAwardDialogOpen(true)}>
                <Gift size={16} className="mr-2" />
                Award Points
              </Button>
            )}
            <Button onClick={fetchData}>
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
        
        {/* Agent Profile Card */}
        <AgentProfileCard profile={profile} weekInfo={weekInfo} />
        
        {/* Mobile Countdown */}
        <div className="lg:hidden">
          <WeeklyCountdown timeRemaining={weekInfo?.time_remaining} />
        </div>
        
        {/* Competition Panels */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <CompetitionPanel
            competition={weekInfo?.competitions?.deal_closer}
            competitionKey="deal_closer"
            points={profile?.weekly_points?.deal_closer}
            rank={leaderboard.findIndex(a => a.agent_id === profile?.agent_id) + 1 || null}
            topAgents={leaderboard}
            breakdown={profile?.points_breakdown}
          />
          <CompetitionPanel
            competition={weekInfo?.competitions?.lead_converter}
            competitionKey="lead_converter"
            points={profile?.weekly_points?.lead_converter}
            rank={leaderboard.findIndex(a => a.agent_id === profile?.agent_id) + 1 || null}
            topAgents={leaderboard}
            breakdown={profile?.points_breakdown}
          />
          <CompetitionPanel
            competition={weekInfo?.competitions?.ai_challenge}
            competitionKey="ai_challenge"
            points={profile?.weekly_points?.ai_challenge}
            rank={leaderboard.findIndex(a => a.agent_id === profile?.agent_id) + 1 || null}
            topAgents={leaderboard}
            breakdown={profile?.points_breakdown}
          />
        </div>
        
        {/* AI Challenge & Leaderboard */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <AIChallengeCard challenge={challenge} streak={challengeStreak} onRefresh={fetchData} />
          </div>
          <div className="lg:col-span-2">
            <FullLeaderboard
              leaderboard={leaderboard}
              currentUserId={profile?.agent_id}
              activeTab={activeLeaderboardTab}
              onTabChange={setActiveLeaderboardTab}
            />
          </div>
        </div>
      </div>
      
      {/* HOD Award Dialog */}
      <HODAwardDialog
        isOpen={isAwardDialogOpen}
        onClose={() => setIsAwardDialogOpen(false)}
        agents={leaderboard}
        onAward={handleAwardPoints}
      />
    </div>
  );
};

export default CompetitionDashboard;
